import { FileText, Package, Receipt } from 'lucide-react';
import type { AssetListItem } from '@/services/assets.service';
import { AuthImage } from '@/components/ui/auth-image';
import { Badge } from '@/components/ui/badge';

const THUMB_CLASS =
  'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded transition hover:ring-2 hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';

function PhotoPlaceholder() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-muted">
      <Package className="h-5 w-5 text-muted-foreground" />
    </div>
  );
}

/** Photo + invoice/bill tiles; a missing bill is highlighted as pending. Both open `onOpen`. */
export function AssetFilesCell({
  row,
  documentBasePath,
  onOpen,
  showInvoice = true,
}: {
  row: AssetListItem;
  /** `/assets/documents` (admin) or `/assets/my/documents` (self-service). */
  documentBasePath: string;
  onOpen: () => void;
  /** My Assets shows the photo only. */
  showInvoice?: boolean;
}) {
  const invoiceIsImage = row.invoice_mime_type?.startsWith('image/') ?? false;

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={onOpen}
        className={THUMB_CLASS}
        aria-label={`Photos of ${row.asset_code}`}
        title="Photos"
      >
        {row.photo_doc_id ? (
          <AuthImage
            src={`${documentBasePath}/${row.photo_doc_id}/file`}
            alt={row.asset_code}
            className="h-full w-full object-cover"
            fallback={<PhotoPlaceholder />}
          />
        ) : (
          <PhotoPlaceholder />
        )}
      </button>
      {!showInvoice ? null : row.invoice_doc_id ? (
        <button
          type="button"
          onClick={onOpen}
          className={`${THUMB_CLASS} border border-border/60 bg-muted/40`}
          aria-label={`Invoice of ${row.asset_code}`}
          title="Invoice / bill"
        >
          {invoiceIsImage ? (
            <AuthImage
              src={`${documentBasePath}/${row.invoice_doc_id}/file`}
              alt={`${row.asset_code} invoice`}
              className="h-full w-full object-cover"
              fallback={<FileText className="h-5 w-5 text-primary" />}
            />
          ) : (
            <FileText className="h-5 w-5 text-primary" />
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={onOpen}
          className={`${THUMB_CLASS} flex-col gap-0.5 border-2 border-dashed border-amber-400 bg-amber-50 text-amber-600 dark:border-amber-500/70 dark:bg-amber-500/10 dark:text-amber-400`}
          aria-label={`Invoice of ${row.asset_code} is pending`}
          title="Invoice / bill pending"
        >
          <Receipt className="h-4 w-4" />
          <span className="text-[8px] font-semibold uppercase leading-none">Bill</span>
        </button>
      )}
    </div>
  );
}

export function AssetCodeCell({ row }: { row: AssetListItem }) {
  return <code className="text-xs font-mono font-semibold">{row.asset_code}</code>;
}

export function AssetCategoryCell({ row }: { row: AssetListItem }) {
  return (
    <div>
      <p className="text-sm">{row.category?.name ?? '—'}</p>
      {row.subcategory && <p className="text-xs text-muted-foreground">{row.subcategory.name}</p>}
    </div>
  );
}

export function AssetBrandModelCell({ row }: { row: AssetListItem }) {
  const text = [row.brand?.name, row.model_name].filter(Boolean).join(' / ');
  return text ? <span className="text-sm">{text}</span> : <span className="text-sm text-muted-foreground">—</span>;
}

/** N/A, "Yes" (no end date), or the end date — red once expired, green while valid. */
export function AssetWarrantyCell({ row }: { row: AssetListItem }) {
  if (!row.warranty_available) return <Badge variant="secondary">N/A</Badge>;
  if (!row.warranty_end_date) return <span className="text-sm text-muted-foreground">Yes</span>;

  const expired = row.warranty_end_date < new Date().toISOString().slice(0, 10);
  return (
    <span
      className={`text-sm font-medium ${
        expired ? 'text-rose-500 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'
      }`}
    >
      {new Date(row.warranty_end_date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })}
    </span>
  );
}
